import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useAxios from "../../hooks/useAxios";
import { Typography, Button, TextField, Card, CardContent, CircularProgress, Alert, Box, Radio, RadioGroup, FormControlLabel, FormControl } from "@mui/material";
import "./SessionPage.css";
import MathPreview from "../../components/CustomInputFields/MathPreview/MathPreview";
import ToggleSwitch from "../../components/ToggleSwitch/ToggleSwitch";
import CustomInputField from "../../components/CustomInputFields/CustomInputField/CustomInputField";
import Header from "../../components/Header/Header";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import Tooltip from "@mui/material/Tooltip";

const navLinks = [
];
const SessionPage = () => {
  const { id: sessionId } = useParams();
  const navigate = useNavigate();
  const axiosInstance = useAxios();

  const [loading, setLoading] = useState(false);
  const [questionLoading, setQuestionLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [questionType, setQuestionType] = useState(null); // 'question', 'subquestion', or null if finished
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentSubquestion, setCurrentSubquestion] = useState(null);

  const [response, setResponse] = useState("");
  const [attempt, setAttempt] = useState(1);
  const [feedback, setFeedback] = useState(null); // { is_correct: bool, hint: string, grade: number }

  const [sessionCompleted, setSessionCompleted] = useState(false);
  const [finalResult, setFinalResult] = useState(null); // { final_grade, correct_answers, incorrect_answers, ... }

  const [isMathAnswer, setIsMathAnswer] = useState(false);

  const [questionChoices, setQuestionChoices] = useState([]);
  const [subQuestionChoices, setSubQuestionChoices] = useState([]);
  const [report, setReport] = useState(null);
  // Fetch the next question or subquestion
  const fetchNextQuestion = async () => {
    setQuestionLoading(true);
    setError(null);
    setFeedback(null);
    setResponse("");
    setAttempt(1);
    setQuestionChoices([]);
    setSubQuestionChoices([]);

    try {
      const res = await axiosInstance.get(`/sessions/${sessionId}/next-question`);
      if (res.data.success && (res.data.type === 'question' || res.data.type === 'subquestion')) {
        setQuestionType(res.data.type);
        setCurrentQuestion(res.data.question);
        setCurrentSubquestion(res.data.subquestion || null);

        // Parse question choices if present
        if (res.data.question && res.data.question.choices) {
          try {
            const parsed = JSON.parse(res.data.question.choices);
            if (Array.isArray(parsed)) {
              setQuestionChoices(parsed);
            }
          } catch (e) {
            console.error("Failed to parse question choices:", e);
          }
        }

        // Parse subquestion choices if present
        if (res.data.subquestion && res.data.subquestion.choices) {
          try {
            const parsed = JSON.parse(res.data.subquestion.choices);
            if (Array.isArray(parsed)) {
              setSubQuestionChoices(parsed);
            }
          } catch (e) {
            console.error("Failed to parse subquestion choices:", e);
          }
        }
      } else {
        // If no more questions, it means we reached the end
        setQuestionType(null);
      }
    } catch (err) {
      console.error("Error fetching next question:", err);
      setError(err.response?.data?.message || "Failed to load question.");
    } finally {
      setQuestionLoading(false);
    }
  };

  // Submit answer
  const submitAnswer = async () => {
    if (!currentQuestion) return;

    setLoading(true);
    setError(null);
    setFeedback(null);

    const questionId = currentQuestion.id;
    const subquestionId = currentSubquestion?.id;

    try {
      const payload = {
        response,
        attempt,
      };
      if (subquestionId) {
        payload.subquestion_id = subquestionId;
      }
      const res = await axiosInstance.post(
        `/sessions/${sessionId}/questions/${questionId}/answer`,
        payload
      );

      // After we get the response, show the feedback
      setFeedback({
        is_correct: res.data.is_correct,
        hint: res.data.hint,
        grade: res.data.grade,
      });
    } catch (err) {
      console.error("Error submitting answer:", err);
      setError(err.response?.data?.error || "Failed to submit answer.");
    } finally {
      setLoading(false);
    }
  };

  // Complete the session and show final results
  const completeSession = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.post(`/sessions/${sessionId}/complete`);
      if (res.data.success) {
        setSessionCompleted(true);
        setFinalResult({
          final_grade: res.data.final_grade,
          correct_answers: res.data.correct_answers,
          incorrect_answers: res.data.incorrect_answers,
          total_questions: res.data.total_questions,
          total_main_questions: res.data.total_main_questions,
          total_subquestions: res.data.total_subquestions,
        });
        
        // <--- NEW: also store the AI report if present
        if (res.data.report) {
          setReport(res.data.report);
        }
      } else {
        setError(res.data.message || "Failed to complete session.");
      }
    } catch (err) {
      console.error("Error completing session:", err);
      setError(err.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  // On mount, fetch the first question
  useEffect(() => {
    fetchNextQuestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const handleNextQuestion = () => {
    // If the answer was correct or we simply want the next question:
    fetchNextQuestion();
  };

  const handleTryAgain = () => {
    // Increase attempt number and allow user to try again
    setAttempt((prev) => (prev < 3 ? prev + 1 : prev));
    setFeedback(null);
  };

  const handleAnswerToggleChange = (event) => {
    setIsMathAnswer(event.target.checked);
  };

  const renderChoices = (choicesArray) => {
    return (
      <RadioGroup
        className="choices-container"
        value={response}
        onChange={(e) => setResponse(e.target.value)}
      >
        {choicesArray.map((choice, index) => (
          <FormControlLabel
            key={index}
            value={choice.value}
            control={<Radio />}
            label={<MathPreview text={choice.value} />}
          />
        ))}
      </RadioGroup>


    );
  };

  if (sessionCompleted) {
    // Show final result summary
    return (
      <>
        <Header navLinks={navLinks} isScrollEffect={false} />
        <div className="session-page-container page-content">
          <Card className="result-card">
            <CardContent>
              <Typography className="announcement" gutterBottom>
                Session Completed
              </Typography>
              <div className="results-container">
                {/* <div className="result-item">
                  <Typography className="results-key">Correct Answers:</Typography>
                  <Typography className="results-value">{finalResult.correct_answers}</Typography>
                </div>
                <div className="result-item">
                  <Typography className="results-key">Incorrect Answers:</Typography>
                  <Typography className="results-value">{finalResult.incorrect_answers}</Typography>
                </div> */}
                <div className="result-item centered">
                  <Typography className="results-key">Final Grade:</Typography>
                  <Typography className="results-value">
                    {finalResult.final_grade.toFixed(1)}%
                  </Typography>
                </div>
              </div>
  
              {/* AI Report Section */}
              {report && (
                <Box mt={4} className="ai-report-container">
                  <Typography variant="h6" gutterBottom className="ai-report-title">
                    Your AI Performance Report
                  </Typography>
                  <div className="ai-report-section">
                    <Typography variant="subtitle1" className="report-label">
                      Strength Points:
                    </Typography>
                    <Typography variant="body1" className="report-text">
                      {report.strength_points || "N/A"}
                    </Typography>
                  </div>
                  <div className="ai-report-section">
                    <Typography variant="subtitle1" className="report-label">
                      Points to Improve:
                    </Typography>
                    <Typography variant="body1" className="report-text">
                      {report.points_to_improve || "N/A"}
                    </Typography>
                  </div>
                  <div className="ai-report-section">
                    <Typography variant="subtitle1" className="report-label">
                      Recommendation:
                    </Typography>
                    <Typography variant="body1" className="report-text">
                      {report.recommendation || "N/A"}
                    </Typography>
                  </div>
                </Box>
              )}
  
              <Button className="secondary-button centered-button" onClick={() => navigate(-1)}>
                Go Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  const hasQuestionChoices = Array.isArray(questionChoices) && questionChoices.length > 0;
  const hasSubQuestionChoices = Array.isArray(subQuestionChoices) && subQuestionChoices.length > 0;

  return (
    <>
    
      <Header navLinks={navLinks} isScrollEffect={false} />
      <div className="session-page-container page-content">
        {questionLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
            <CircularProgress />
          </Box>
        )}

        {!questionLoading && questionType === null && !sessionCompleted && (
          <Card className="question-card">
            <CardContent>
              <Typography className="announcement" variant="h5" gutterBottom>Quiz Completed!</Typography>
              <Typography className="centered-text">Click below to complete the session and view your results.</Typography>
              <Button 
                className="primary-button centered-button" 
                onClick={completeSession}
                disabled={loading}
                style={{marginTop: '1em'}}
              >
                {loading ? "Completing..." : "Complete Session"}
              </Button>
            </CardContent>
          </Card>
        )}

        {!questionLoading && questionType && (
          <Card className="question-card">
            <CardContent>
              {error && <Alert severity="error" style={{marginBottom: "1em"}}>{error}</Alert>}
              

              <Typography variant="h5" gutterBottom>
                {questionType === "subquestion" ? "Subquestion" : "Question"}
              </Typography>

              {currentQuestion && (
                <div className="question-container">
                  <Typography variant="body1">
                    <MathPreview text={currentQuestion?.body} />
                  </Typography>
                  {currentQuestion?.image && (
                    <img src={currentQuestion?.image} alt="question" className="question-image" />
                  )}

                  {hasQuestionChoices && renderChoices(questionChoices)}
                </div>
              )}

              {currentSubquestion && (
                <div className="question-container" style={{marginTop: "1em"}}>
                  <Typography variant="body1">
                    <strong>Subquestion:</strong> <MathPreview text={currentSubquestion.body} />
                  </Typography>
                  {currentSubquestion?.image && <img src={currentSubquestion.image} alt="subquestion" className="question-image" />}

                  {hasSubQuestionChoices && renderChoices(subQuestionChoices)}
                </div>
              )}
              {feedback && (
                <Alert severity={feedback.is_correct ? "success" : "warning"} style={{marginBottom: "1em"}}>
                  {feedback.is_correct ? "Correct!" : "Incorrect"}
                  {feedback.hint && <div>Hint: {feedback.hint}</div>}
                  {typeof feedback.grade === 'number' && <div>Grade: {feedback.grade.toFixed(1)}</div>}
                </Alert>
              )}

              {!feedback && !hasQuestionChoices && !hasSubQuestionChoices && (
                <div className="answer-form">
                  <ToggleSwitch
                    mainLabel="answer mode"
                    labelLeft="Regular"
                    labelRight="Math"
                    checked={isMathAnswer}
                    onChange={handleAnswerToggleChange}
                  />
                  { isMathAnswer ?
                    <CustomInputField
                      body={response}
                      onBodyChange={(value) => setResponse(value)}
                      label={"Your Answer"}
                      isbigger={true}
                    />
                  :
                    <TextField
                      label="Your Answer"
                      value={response}
                      onChange={(e) => setResponse(e.target.value)}
                      fullWidth
                      multiline
                      rows={3}
                      className="custom-text-field"
                      style={{marginBottom: "1em"}}
                    />
                  }    

                  <Button 
                    onClick={submitAnswer} 
                    disabled={loading || !response}
                    className="primary-button"
                  >
                    {loading ? "Submitting..." : "Submit Answer"}
                  </Button>
                </div>
              )}

              {!feedback && (hasQuestionChoices || hasSubQuestionChoices) && (
                <div style={{ marginTop: '1em' }}>
                  <Button 
                    className="primary-button centered-button"
                    onClick={submitAnswer} 
                    disabled={loading || !response}
                  >
                    {loading ? "Submitting..." : "Submit Answer"}
                  </Button>
                </div>
              )}

              {feedback && (
                <div className="answer-result-actions" style={{marginTop: "1em"}}>
                  {feedback.is_correct ? (
                    <Button className="secondary-button centered-button" onClick={handleNextQuestion}>
                      Next Question
                    </Button>
                  ) : (
                    attempt < 3 ? (
                      <Button className="secondary-button centered-button" onClick={handleTryAgain}>
                        Try Again (Attempt {attempt + 1})
                      </Button>
                    ) : (
                      <Button className="secondary-button centered-button" onClick={handleNextQuestion}>
                        Next Question
                      </Button>
                    )
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

         {/* Floating Complete Session Button */}
         <div className="floating-complete-session">
          <Tooltip title="End Session and Exit" arrow>
            <Button
              className="primary-button floating-button"
              onClick={completeSession}
              disabled={loading || sessionCompleted}
            >
              <ExitToAppIcon style={{ fontSize: 24 }} />
            </Button>
          </Tooltip>
        </div>
      </div>
    </>
  );
};

export default SessionPage;
